import classNames from 'classnames';
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  Suspense,
} from 'react';
import { memberId } from '@trello/session-cookie';
import { DownIcon } from '@trello/nachos/icons/down';
import { useSharedState } from '@trello/shared-state';
import { useLazyComponent } from '@trello/use-lazy-component';
import { forTemplate } from '@trello/i18n';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { RouterLink } from 'app/src/components/RouterLink';
import styles from './EnterpriseHeaderLogo.less';
import { navigationState } from './navigationState';
import { HeaderLogo } from './HeaderLogo';
import { useBoardBackground } from 'app/src/components/Theme';

import { useEnterpriseHeaderLogoQuery } from './EnterpriseHeaderLogoQuery.generated';

const format = forTemplate('header_back_menu_button');

interface EnterpriseHeaderLogoContentProps {
  displayName: string;
  analyticsAttributes: object;
}

const EnterpriseHeaderLogoContent = ({
  displayName,
  analyticsAttributes,
}: EnterpriseHeaderLogoContentProps) => {
  const { backgroundBrightness } = useBoardBackground();

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentType: 'button',
      componentName: 'enterpriseHeaderLogoNameButton',
      source: getScreenFromUrl(),
      attributes: analyticsAttributes,
    });
  }, [analyticsAttributes]);

  const [{ isNavigating }] = useSharedState(navigationState);
  const EnterpriseHeaderLogoOverlayContent = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "enterprise-header-logo-overlay-content" */ './EnterpriseHeaderLogoOverlayContent'
      ),
    {
      namedImport: 'EnterpriseHeaderLogoOverlayContent',
    },
  );

  const logo = (
    <div
      className={classNames(styles.logoIconContainer, styles.hoverBackground)}
    >
      <div className={styles.logoIconCrop}>
        <div
          className={classNames(styles.logoIcon)}
          data-loading={isNavigating}
        />
      </div>
    </div>
  );

  const [showOverlay, setShowOverlay] = useState(false);
  const overlayOpenedTimestamp = useRef<Date | null>(null);
  const onClickEnterpriseName = useCallback(() => {
    setShowOverlay(true);
    overlayOpenedTimestamp.current = new Date();
    Analytics.sendClickedButtonEvent({
      buttonName: 'enterpriseHeaderLogoNameButton',
      source: getScreenFromUrl(),
      attributes: analyticsAttributes,
    });
  }, [analyticsAttributes]);

  const onClickEnterpriseLogoIcon = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'enterpriseHeaderLogoIcon',
      source: getScreenFromUrl(),
      attributes: analyticsAttributes,
    });
  }, [analyticsAttributes]);

  const closeOverlay = useCallback(() => {
    setShowOverlay(false);
    const elapsedTimeOpen = overlayOpenedTimestamp.current
      ? new Date().getTime() - overlayOpenedTimestamp.current.getTime()
      : null;
    Analytics.sendUIEvent({
      action: 'dismissed',
      actionSubject: 'modal',
      actionSubjectId: 'enterpriseCollaborationFeedbackModal',
      source: 'enterpriseCollaborationFeedbackModal',
      attributes: {
        ...analyticsAttributes,
        elapsedTimeOpen,
      },
    });
    overlayOpenedTimestamp.current = null;
  }, [analyticsAttributes]);

  return (
    <div className={styles.fullLogo}>
      <RouterLink
        href="/"
        onClick={onClickEnterpriseLogoIcon}
        title="Trello"
        aria-label={format('backToHome')}
      >
        {logo}
      </RouterLink>
      <button
        onClick={onClickEnterpriseName}
        className={classNames(styles.hoverBackground, styles.logoText)}
        tabIndex={0}
      >
        <h1 title={displayName}>{displayName}</h1>
        <span className={styles.downIcon}>
          <DownIcon
            size="small"
            color={backgroundBrightness === 'dark' ? 'light' : 'dark'}
          />
        </span>
      </button>
      {showOverlay && (
        <Overlay
          onClose={closeOverlay}
          background={OverlayBackground.DARK}
          alignment={OverlayAlignment.TOP}
          closeOnEscape={false}
        >
          <Suspense fallback={null}>
            <EnterpriseHeaderLogoOverlayContent
              displayName={displayName}
              closeOverlay={closeOverlay}
              analyticsAttributes={analyticsAttributes}
            />
          </Suspense>
        </Overlay>
      )}
    </div>
  );
};

export const EnterpriseHeaderLogo = () => {
  const { data } = useEnterpriseHeaderLogoQuery({
    variables: {
      id: memberId ?? 'me',
    },
    skip: !memberId,
  });

  const displayName = data?.member?.ofEnterprise?.displayName;
  const analyticsAttributes = useMemo(() => {
    return {
      organizationSource: data?.member?.ofEnterprise?.source,
    };
  }, [data?.member?.ofEnterprise?.source]);

  return displayName ? (
    <EnterpriseHeaderLogoContent
      displayName={displayName}
      analyticsAttributes={analyticsAttributes}
    />
  ) : (
    <HeaderLogo />
  );
};
